import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Ticket, BookOpen, Shield, Plus, LogOut, FolderKanban, NotebookPen, Package, Menu, X, Settings, BarChart3 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';
import api from '../../lib/api';
import RatingPromptBanner from '../tickets/RatingPromptBanner';

const NAV = [
  { label: 'Dashboard',     icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Tickets',       icon: Ticket,          path: '/tickets' },
  { label: 'Projects',      icon: FolderKanban,    path: '/projects' },
  { label: 'Knowledgebase', icon: BookOpen,        path: '/knowledgebase' },
];

export default function AppShell() {
  const { user, isStaff, logout } = useAuth();
  const { loadUserPrefs } = useTheme();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [dueCount, setDueCount]       = useState(0);
  const [assetAlerts, setAssetAlerts] = useState(0);
  const [mobileOpen, setMobileOpen]   = useState(false);

  useEffect(() => {
    if (user) loadUserPrefs(user);
  }, [user?.id]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isStaff) return;
    function check() {
      api.get('/workbook/reminders/due-count').then(d => setDueCount(d.count || 0)).catch(() => {});
      api.get('/assets/alert-count').then(d => setAssetAlerts(d.count || 0)).catch(() => {});
    }
    check();
    const interval = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isStaff]);

  const handleSignOut = () => {
    logout();
    toast.success('Signed out');
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const initials = (user?.full_name || user?.email || '?')
    .split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="app-shell">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:199 }}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className="sidebar" style={{ left: mobileOpen ? 0 : undefined }}>
        <div className="sidebar-logo">
          <h1>GT Computing</h1>
          <span>Help Desk Portal</span>
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-section-label">Navigation</div>
          {NAV.map(item => (
            <button key={item.path} className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => navigate(item.path)}>
              <item.icon size={16} />{item.label}
            </button>
          ))}

          {isStaff && (
            <>
              <div className="sidebar-section-label" style={{ marginTop: 8 }}>Staff</div>
              <button className={`nav-item ${isActive('/workbook') ? 'active' : ''}`}
                onClick={() => navigate('/workbook')}>
                <NotebookPen size={16} />My Workbook
                {dueCount > 0 && (
                  <span style={{ marginLeft:'auto', background:'#ef4444', color:'#fff', borderRadius:10, padding:'1px 6px', fontSize:10, fontWeight:700, lineHeight:'14px' }}>
                    {dueCount > 9 ? '9+' : dueCount}
                  </span>
                )}
              </button>
              <button className={`nav-item ${isActive('/assets') ? 'active' : ''}`}
                onClick={() => navigate('/assets')}>
                <Package size={16} />Assets
                {assetAlerts > 0 && (
                  <span style={{ marginLeft:'auto', background:'#f59e0b', color:'#fff', borderRadius:10, padding:'1px 6px', fontSize:10, fontWeight:700, lineHeight:'14px' }}>
                    {assetAlerts > 9 ? '9+' : assetAlerts}
                  </span>
                )}
              </button>
              <button className={`nav-item ${isActive('/reports') ? 'active' : ''}`} onClick={() => navigate('/reports')}>
                <BarChart3 size={16} />Reports
              </button>
              <button className={`nav-item ${isActive('/admin') ? 'active' : ''}`} onClick={() => navigate('/admin')}>
                <Shield size={16} />Admin Panel
              </button>
            </>
          )}

          <div style={{ marginTop: 'auto', paddingTop: 12 }}>
            <button className="btn btn-primary w-full" style={{ justifyContent:'center', background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.2)', color:'#fff' }}
              onClick={() => navigate('/tickets/new')}>
              <Plus size={15} /> New Ticket
            </button>
          </div>
        </nav>

        <div className="sidebar-footer">
          <button className="nav-item w-full" onClick={() => navigate('/profile')} style={{ marginBottom: 2 }}>
            <div className="avatar" style={{ width:24, height:24, fontSize:10, background:'rgba(255,255,255,0.2)', color:'#fff' }}>{initials}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div className="truncate font-medium" style={{ fontSize:13 }}>{user?.full_name || user?.email}</div>
              <div style={{ fontSize:11, opacity:0.5, textTransform:'capitalize' }}>{user?.role?.replace('_',' ')}</div>
            </div>
          </button>
          <button className="nav-item w-full" onClick={() => navigate('/preferences')}>
            <Settings size={15} />Preferences
          </button>
          <button className="nav-item w-full" onClick={handleSignOut}>
            <LogOut size={15} />Sign out
          </button>
        </div>
      </aside>

      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, overflow:'hidden' }}>
        <RatingPromptBanner />

        {/* Mobile top bar */}
        <div style={{ display:'flex', alignItems:'center', gap:12, padding:'0 16px', height:52, borderBottom:'1px solid var(--border)', background:'var(--bg-card)', flexShrink:0 }}
          className="mobile-topbar">
          <button style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:6, border:'none', background:'none', color:'var(--text-secondary)', cursor:'pointer', borderRadius:6 }}
            onClick={() => setMobileOpen(true)}>
            <Menu size={20} />
          </button>
          <span style={{ fontWeight:600, fontSize:15 }}>GT Computing</span>
        </div>

        <main className="main-content"><Outlet /></main>
      </div>
    </div>
  );
}
